import React, { useState } from 'react';
import { useAppContext } from '../App';
import { Search, Shield, Trash2, Users } from 'lucide-react';
import { UserRole } from '../types';

const UserManagement: React.FC = () => {
  const { allUsers, updateUserRole, deleteUser, churches, currentUser } = useAppContext();
  const [searchTerm, setSearchTerm] = useState('');
  
  // Logic: Admin tools are scoped to the specific church.
  // Both Pastors and Admins can only manage members of their own church.
  const myChurch = churches.find(c => c.id === currentUser?.churchId);

  const relevantUsers = allUsers.filter(u => u.churchId === currentUser?.churchId);

  const filteredUsers = relevantUsers.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 flex flex-col md:flex-row justify-between items-end md:items-center gap-4">
            <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <Shield className="text-blue-600" /> Admin Tools: User Management
                </h1>
                <p className="text-gray-500 mt-1">
                    Manage members and roles for <span className="font-bold text-gray-800">{myChurch ? myChurch.name : 'your church'}</span>.
                </p>
            </div>
            <div className="relative w-full md:w-64">
                <input 
                    type="text" 
                    placeholder="Search members..." 
                    className="w-full pl-10 pr-4 py-2 bg-transparent border-b border-gray-300 rounded-none focus:outline-none focus:border-blue-600 transition-colors placeholder-gray-400"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
            </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">User</th>
                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Church Role</th>
                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">System Role</th>
                            <th className="px-6 py-3 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredUsers.map(user => {
                            const isSelf = user.id === currentUser?.id;

                            return (
                                <tr key={user.id} className="hover:bg-gray-50 transition">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="flex-shrink-0 h-10 w-10">
                                                <img className="h-10 w-10 rounded-full bg-gray-200 object-cover" src={user.avatarUrl} alt="" />
                                            </div>
                                            <div className="ml-4">
                                                <div className="text-sm font-medium text-gray-900">{user.name} {isSelf && "(You)"}</div>
                                                <div className="text-sm text-gray-500">{user.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className="text-sm text-gray-600">{user.voluntaryCall || 'Member'}</span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <select 
                                            value={user.role}
                                            onChange={(e) => updateUserRole(user.id, e.target.value as UserRole)}
                                            disabled={isSelf}
                                            className={`text-sm rounded-full px-3 py-1 font-semibold border-0 ring-1 ring-inset focus:ring-2 focus:ring-blue-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${
                                                user.role === UserRole.ADMIN ? 'bg-purple-50 text-purple-700 ring-purple-600/20' :
                                                user.role === UserRole.PASTOR ? 'bg-blue-50 text-blue-700 ring-blue-600/20' :
                                                'bg-gray-50 text-gray-600 ring-gray-500/10'
                                            }`}
                                        >
                                            <option value={UserRole.MEMBER}>Member</option>
                                            <option value={UserRole.PASTOR}>Pastor</option>
                                            <option value={UserRole.ADMIN}>Admin</option>
                                        </select>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button 
                                            onClick={() => {
                                                if(window.confirm(`Are you sure you want to remove ${user.name} from the church?`)) {
                                                    deleteUser(user.id);
                                                }
                                            }}
                                            disabled={isSelf}
                                            className="text-gray-400 hover:text-red-600 transition disabled:opacity-30 disabled:hover:text-gray-400" 
                                            title="Remove User"
                                        >
                                            <Trash2 className="w-5 h-5" />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {filteredUsers.length === 0 && (
                    <div className="p-10 text-center text-gray-500">
                        <Users className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                        <p>No members found in your church.</p>
                    </div>
                )}
            </div>
        </div>
    </div>
  );
};

export default UserManagement;